"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSensors = void 0;
const react_1 = require("react");
const mutations_1 = require("../graphql/mutations");
const queries_1 = require("../graphql/queries");
const subscriptions_1 = require("../graphql/subscriptions");
const types_1 = require("../types");
const amplifyBootstrap_1 = require("../utils/amplifyBootstrap");
/**
 * Backstop: Amplify v6 already rejects when the response carries `errors`.
 * A refusal swallowed by an older stage's resolver template arrives as a plain
 * `null` instead, so callers still treat a null result as a failure.
 */
function throwIfGraphqlErrors(response, op) {
    if (response?.errors?.length) {
        console.error(`GraphQL errors in ${op}:`, response.errors);
        throw new Error(response.errors[0]?.message || `Failed: ${op}`);
    }
}
const useSensors = (hubId) => {
    const [sensors, setSensors] = (0, react_1.useState)([]);
    const [hub, setHub] = (0, react_1.useState)();
    const [loading, setLoading] = (0, react_1.useState)(null);
    const [error, setError] = (0, react_1.useState)(null);
    const subscriptionsRef = (0, react_1.useRef)([]);
    const fetchHub = (0, react_1.useCallback)(async (id) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getHub,
                variables: { id },
            });
            if (response && response.data && response.data.getHub) {
                setHub(response.data.getHub);
                return response.data.getHub;
            }
            return null;
        }
        catch (error) {
            console.error('Error fetching hub:', error);
        }
    }, []);
    const fetchSensorsByHub = (0, react_1.useCallback)(async (hubId) => {
        setLoading(true);
        setError(null);
        try {
            const hub = await fetchHub(hubId);
            if (!hub) {
                throw new Error(`Hub not found: ${hubId}`);
            }
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorsByHub,
                variables: { hubId, limit: 100 },
            });
            const sensors = response.data?.getSensorsByHub || [];
            setSensors(sensors);
            return sensors;
        }
        catch (error) {
            console.error('Error fetching sensors:', error);
            setError(error);
            setSensors([]);
            return [];
        }
        finally {
            setLoading(false);
        }
    }, [fetchHub]);
    const fetchSensorsByGroupId = (0, react_1.useCallback)(async (groupId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensorsByGroupId,
                variables: { groupId, limit: 100 },
            });
            const sensors = response.data?.getSensorsByGroupId || [];
            setSensors(sensors);
        }
        catch (error) {
            console.error('Error fetching sensors:', error);
            setError(error);
            setSensors([]);
        }
        finally {
            setLoading(false);
        }
    }, []);
    const fetchAllSensors = (0, react_1.useCallback)(async (limit = 100) => {
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.listSensors,
                variables: { limit },
            });
            const sensors = response.data?.listSensors || [];
            setSensors(sensors);
        }
        catch (error) {
            console.error('Error fetching sensors:', error);
            setError(error);
            setSensors([]);
        }
        finally {
            setLoading(false);
        }
    }, []);
    const getSensorById = (0, react_1.useCallback)(async (id) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getSensor,
                variables: { id },
            });
            return response.data.getSensor;
        }
        catch (error) {
            console.error('Error fetching sensor:', error);
            throw error;
        }
    }, []);
    const addSensor = (0, react_1.useCallback)(async (inputOrParams) => {
        try {
            let sensorInput;
            if ('hwVersion' in inputOrParams && 'sensorType' in inputOrParams) {
                const { hubId, dsk, hwVersion, sensorType, serialNumber, name, location, sublocation, lastReported, equipmentDetails, } = inputOrParams;
                sensorInput = {
                    hubId,
                    dsk,
                    type: sensorType,
                    version: hwVersion,
                    serialNumber,
                    name: name || `Sensor ${serialNumber}`,
                    location: location || '',
                    status: types_1.SensorStatus.Inactive,
                    sublocation,
                    lastReported,
                    equipmentDetails,
                };
            }
            else {
                sensorInput = {
                    ...inputOrParams,
                    status: inputOrParams.status || types_1.SensorStatus.Inactive,
                    location: inputOrParams.location || '',
                    name: inputOrParams.name || `Sensor ${inputOrParams.serialNumber}`,
                    version: inputOrParams.version,
                };
            }
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.createSensor,
                variables: { input: sensorInput },
            });
            throwIfGraphqlErrors(response, 'createSensor');
            return response.data.createSensor;
        }
        catch (error) {
            console.error('Error creating sensor:', error);
            throw error;
        }
    }, []);
    const createSensorGroup = (0, react_1.useCallback)(async (input) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.createSensorGroup,
                variables: { input },
            });
            throwIfGraphqlErrors(response, 'createSensorGroup');
            return response.data.createSensorGroup;
        }
        catch (error) {
            console.error('Error creating sensor group:', error);
            throw error;
        }
    }, []);
    const updateSensor = (0, react_1.useCallback)(async (input) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.updateSensor,
                variables: { input },
            });
            throwIfGraphqlErrors(response, 'updateSensor');
            const updatedSensor = response.data.updateSensor;
            if (updatedSensor) {
                setSensors((prev) => prev.map((s) => (s.id === updatedSensor.id ? { ...s, ...updatedSensor } : s)));
            }
            return updatedSensor;
        }
        catch (error) {
            console.error('Error updating sensor:', error);
            throw error;
        }
    }, []);
    const updateSensorBranchCircuits = (0, react_1.useCallback)(async (input) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.updateSensorBranchCircuits,
                variables: { input },
            });
            if (response.errors?.length) {
                console.error('GraphQL errors updating branch circuits:', response.errors);
                throw new Error(response.errors[0]?.message || 'Failed to update branch circuits');
            }
            const updatedSensor = response.data.updateSensorBranchCircuits;
            if (updatedSensor) {
                setSensors((prev) => prev.map((s) => (s.id === updatedSensor.id ? { ...s, ...updatedSensor } : s)));
            }
            return updatedSensor;
        }
        catch (error) {
            console.error('Error updating sensor branch circuits:', error);
            throw error;
        }
    }, []);
    /**
     * Soft-deletes the row. `unitState` is the installer's answer (omitted = no
     * reason: the base station removes the node on its own). The returned row
     * carries `removal` when the base station still has a unit to unpair: that
     * is step 2.
     */
    const removeSensor = (0, react_1.useCallback)(async (id, unitState) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.deleteSensor,
                variables: { id, unitState },
            });
            throwIfGraphqlErrors(response, 'deleteSensor');
            return response.data.deleteSensor;
        }
        catch (error) {
            console.error('Error deleting sensor:', error);
            throw error;
        }
    }, []);
    /**
     * "I did the reset" (no unitState) or "Sensor is broken or lost" (BROKEN) on
     * a deleted row whose removal is open: asks the base station again. Returns
     * the row; `removal.closedAt` set means it closed on the spot.
     */
    const retrySensorRemoval = (0, react_1.useCallback)(async (id, unitState) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.retrySensorRemoval,
                variables: { id, unitState },
            });
            throwIfGraphqlErrors(response, 'retrySensorRemoval');
            return response.data.retrySensorRemoval;
        }
        catch (error) {
            console.error('Error retrying sensor removal:', error);
            throw error;
        }
    }, []);
    const ungroupSensors = (0, react_1.useCallback)(async (sensorIds) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.deleteSensorLink,
                variables: { input: { sensorIds } },
            });
            throwIfGraphqlErrors(response, 'deleteSensorLink');
            return response.data.deleteSensorLink;
        }
        catch (error) {
            console.error('Error ungrouping sensors:', error);
            throw error;
        }
    }, []);
    const retrySensorInclusion = (0, react_1.useCallback)(async (sensorId) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.retrySensorInclusion,
                variables: { sensorId },
            });
            throwIfGraphqlErrors(response, 'retrySensorInclusion');
            return response.data.retrySensorInclusion;
        }
        catch (error) {
            console.error('Error retrying sensor inclusion:', error);
            throw error;
        }
    }, []);
    const identifySensor = (0, react_1.useCallback)(async (sensorId) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.identifySensor,
                variables: { sensorId },
            });
            throwIfGraphqlErrors(response, 'identifySensor');
            return response.data.identifySensor;
        }
        catch (error) {
            console.error('Error identifying sensor:', error);
            throw error;
        }
    }, []);
    /**
     * Commits a DRAFT row: the hub is told about the sensor and the DSK enters
     * SmartStart. Until this runs the row is app-side bookkeeping only, which is
     * what lets an installer scan a unit, walk away and come back to it.
     */
    const beginSensorInclusion = (0, react_1.useCallback)(async (sensorId) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.beginSensorInclusion,
                variables: { sensorId },
            });
            throwIfGraphqlErrors(response, 'beginSensorInclusion');
            const updated = response.data.beginSensorInclusion;
            if (updated) {
                setSensors((prev) => prev.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)));
            }
            return updated;
        }
        catch (error) {
            console.error('Error beginning sensor inclusion:', error);
            throw error;
        }
    }, []);
    /**
     * Records the installer's sign-off. The identity is taken server-side, so
     * there is nothing to pass. Merged into local state by hand: this mutation
     * writes DynamoDB directly rather than going through `updateSensor`, so no
     * onUpdateSensor subscription fires for it.
     */
    const completeSensorInstall = (0, react_1.useCallback)(async (sensorId) => {
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.completeSensorInstall,
                variables: { sensorId },
            });
            throwIfGraphqlErrors(response, 'completeSensorInstall');
            const updated = response.data.completeSensorInstall;
            if (updated) {
                setSensors((prev) => prev.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)));
            }
            return updated;
        }
        catch (error) {
            console.error('Error completing sensor install:', error);
            throw error;
        }
    }, []);
    const clear = (0, react_1.useCallback)(() => {
        setSensors([]);
        setLoading(null);
    }, []);
    const unsubscribe = (0, react_1.useCallback)(() => {
        if (subscriptionsRef.current.length === 0) {
            return;
        }
        try {
            subscriptionsRef.current.forEach((subscription) => {
                if (subscription && typeof subscription.unsubscribe === 'function') {
                    subscription.unsubscribe();
                }
            });
            subscriptionsRef.current = [];
        }
        catch (error) {
            console.error('Error cleaning up subscriptions:', error);
        }
    }, []);
    const fetchAndSubscribe = (0, react_1.useCallback)(() => {
        if (!hubId) {
            return;
        }
        fetchSensorsByHub(hubId);
        try {
            const createSubscription = amplifyBootstrap_1.API.graphql({
                query: subscriptions_1.onCreateSensor,
            }).subscribe({
                next: ({ data }) => {
                    const newSensor = data.onSensorCreated;
                    if (newSensor && newSensor.hubId === hubId) {
                        setSensors((prevSensors) => [...prevSensors, newSensor]);
                        fetchHub(hubId);
                    }
                },
                error: (error) => console.error('Create subscription error:', error),
            });
            const updateSubscription = amplifyBootstrap_1.API.graphql({
                query: subscriptions_1.onUpdateSensor,
            }).subscribe({
                next: ({ data }) => {
                    const updatedSensor = data.onSensorUpdated;
                    if (updatedSensor) {
                        setSensors((prevSensors) => prevSensors.map((sensor) => sensor.id === updatedSensor.id ? { ...sensor, ...updatedSensor } : sensor));
                        fetchHub(hubId);
                    }
                },
                error: (error) => console.error('Update subscription error:', error),
            });
            const deleteSubscription = amplifyBootstrap_1.API.graphql({
                query: subscriptions_1.onDeleteSensor,
            }).subscribe({
                next: ({ data }) => {
                    const deletedSensor = data.onSensorDeleted;
                    if (deletedSensor) {
                        setSensors((prevSensors) => prevSensors.filter((sensor) => sensor.id !== deletedSensor.id));
                        fetchHub(hubId);
                        console.log('Sensor deleted:', deletedSensor);
                    }
                },
                error: (error) => console.error('Delete subscription error:', error),
            });
            subscriptionsRef.current = [createSubscription, updateSubscription, deleteSubscription];
        }
        catch (error) {
            console.error('Error setting up subscriptions:', error);
        }
    }, [hubId, fetchSensorsByHub, fetchHub]);
    (0, react_1.useEffect)(() => {
        unsubscribe();
        fetchAndSubscribe();
        return () => {
            unsubscribe();
        };
    }, [hubId]);
    return {
        sensors,
        hub,
        loading,
        error,
        getSensorById,
        addSensor,
        createSensorGroup,
        updateSensor,
        updateSensorBranchCircuits,
        removeSensor,
        retrySensorRemoval,
        retrySensorInclusion,
        identifySensor,
        beginSensorInclusion,
        completeSensorInstall,
        ungroupSensors,
        clear,
        fetchAndSubscribe,
        fetchSensorsByHub,
        fetchAllSensors,
        fetchSensorsByGroupId,
        unsubscribe,
    };
};
exports.useSensors = useSensors;
